// src/theme.js
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import {
  FIELD_BLACK,
  SIGNAL_ORANGE,
  SIGNAL_ORANGE_BORDER,
  SIGNAL_ORANGE_DIM,
  SIGNAL_ORANGE_GHOST,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  SURFACE_LOWEST,
  SURFACE_LOW,
  SURFACE_CONTAINER,
  SURFACE_HIGH,
  SURFACE_HIGHEST,
  R,
  LH_TIGHT,
  LH_SNUG,
  LH_BASE,
  LS_TIGHT,
  LS_WIDE,
  LS_CAPS,
  GLASS_BACKDROP,
  GLASS_BG,
} from './design/stoneTokens';

let theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: SIGNAL_ORANGE,
      light: SIGNAL_ORANGE_GHOST,
      dark: SIGNAL_ORANGE_DIM,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ffffff', 
      light: '#ffffff',
      dark: '#cccccc',
      contrastText: FIELD_BLACK,
    },
    tertiary: {
      main: SIGNAL_ORANGE_DIM,
      light: SIGNAL_ORANGE_GHOST,
      dark: '#e07830',
    },
    // The Signal — TogeeVision Slicing Protocol orange accent
    signal: {
      main: SIGNAL_ORANGE,
      dim: SIGNAL_ORANGE_DIM,
      ghost: SIGNAL_ORANGE_GHOST,
      contrastText: '#ffffff',
    },
    background: {
      default: FIELD_BLACK,
      paper: SURFACE_CONTAINER,
    },
    surface: {
      lowest: SURFACE_LOWEST,
      low: SURFACE_LOW,
      main: SURFACE_CONTAINER,
      high: SURFACE_HIGH,
      highest: SURFACE_HIGHEST,
    },
    text: {
      primary: TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif',
    // All line heights breathe at the R=16 rhythm
    h1: {
      fontFamily: '"Noto Serif", serif',
      fontStyle: 'italic',
      fontWeight: 700,
      letterSpacing: LS_TIGHT,
      lineHeight: LH_TIGHT,
    },
    h2: {
      fontFamily: '"Noto Serif", serif',
      fontStyle: 'italic',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: LH_TIGHT,
    },
    h3: {
      fontFamily: '"Noto Serif", serif',
      fontStyle: 'italic',
      fontWeight: 600,
      lineHeight: LH_SNUG,
    },
    h4: {
      fontFamily: '"Noto Serif", serif',
      fontStyle: 'italic',
      fontWeight: 500,
      lineHeight: LH_SNUG,
    },
    h5: {
      fontFamily: '"Noto Serif", serif',
      fontStyle: 'italic',
      fontWeight: 500,
      lineHeight: LH_BASE,
    },
    h6: {
      fontFamily: '"Noto Serif", serif',
      fontStyle: 'italic',
      fontWeight: 500,
      lineHeight: LH_BASE,
    },
    body1: {
      lineHeight: LH_BASE,
    },
    body2: {
      lineHeight: LH_BASE,
    },
    button: {
      fontFamily: '"Space Grotesk", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: LS_WIDE,
    },
    overline: {
      fontFamily: '"JetBrains Mono", monospace',
      letterSpacing: LS_CAPS,
      fontWeight: 700,
      lineHeight: LH_BASE,
    },
    caption: {
      letterSpacing: LS_WIDE,
    },
  },
  spacing: 8, // 1 unit = 8px (R_HALF; 2 units = R = 16px, matching AGENTS.md mobile padding budgets)
  shape: {
    borderRadius: 8, // Sharper default base radius (was 16)
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: FIELD_BLACK,
          color: TEXT_PRIMARY,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: SIGNAL_ORANGE,
          },
        },
        '.glass-panel': {
          backgroundColor: GLASS_BG,
          backdropFilter: GLASS_BACKDROP,
          WebkitBackdropFilter: GLASS_BACKDROP,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(224, 120, 48, 0.14)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // remove MUI's default overlay
          borderRadius: 8, // Sharper cards/papers
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: '8px',
          width: 'calc(100% - 16px)',
          maxWidth: 'min(960px, calc(100% - 16px))',
          maxHeight: 'calc(100dvh - 16px)',
          borderRadius: 14,
          border: `1px solid ${SIGNAL_ORANGE_BORDER}`,
          background: SURFACE_CONTAINER,
          boxShadow: '0 40px 96px -32px rgba(0,0,0,0.82)',
          '@media (min-width:600px)': {
            margin: '24px',
            width: 'calc(100% - 48px)',
            maxWidth: 'min(1100px, calc(100% - 48px))',
            maxHeight: 'calc(100dvh - 48px)',
            borderRadius: 18,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 16px 10px',
          '@media (min-width:600px)': {
            padding: '22px 24px 14px',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '0 16px 16px',
          '@media (min-width:600px)': {
            padding: '0 24px 20px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 8,
          padding: '10px 16px 16px',
          '@media (min-width:600px)': {
            gap: 10,
            padding: '12px 24px 22px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

export default theme;
